import { useEffect } from "react";
import type { VisibleInsets } from "./useVisibleViewport";

/*
 * Every overlay MUI portals to the body: Popover (so also Menu and Select),
 * and the Popper-based ones (Autocomplete, the date/time pickers, tooltips),
 * which all carry `MuiPopper-root`.
 */
const OVERLAY_SELECTOR = ".MuiPopover-root, .MuiPopper-root";

/** The surface inside an overlay root that is actually drawn. */
const SURFACE_SELECTOR = ".MuiPaper-root, .MuiTooltip-tooltip";

/** Breathing room kept between a clamped overlay and the edge of the band. */
const EDGE_GAP = 8;

/**
 * How far up an overlay has to move to sit inside the visible band.
 *
 * Never moves it further than the room above it: a list taller than the band
 * gets its top edge pinned and stays scrollable rather than having its anchor
 * end pushed off screen.
 */
export const overlayClampOffset = (
  surface: { top: number; bottom: number },
  band: { top: number; bottom: number },
  gap = EDGE_GAP,
): number => {
  const overflow = surface.bottom - (band.bottom - gap);
  if (overflow <= 0) return 0;

  const room = surface.top - (band.top + gap);
  return Math.max(0, Math.min(overflow, room));
};

/**
 * Pulls open dropdowns, menus and pickers back into the part of the viewport
 * that is on screen.
 *
 * MUI keeps an overlay inside the viewport it can measure — Popover clamps
 * against `window.innerHeight`, Popper flips against the same box — and in a
 * host frame that is taller than the area it shows, that box extends past the
 * bottom of the screen. Measured against the Loop Hubs embed, the store select
 * opened 262px below the fold: correctly placed as far as the browser was
 * concerned, invisible as far as the user was concerned. The theme's band-sized
 * `maxHeight` on menu papers covers the tall-list case; this covers the rest,
 * where a short list simply opens too low.
 *
 * The shift is a negative `marginTop` on the overlay's root, which is the one
 * property nothing else writes: MUI puts the position in the paper's inline
 * `top`, and Popper owns the root's `transform`. Both are free to recompute
 * without losing the correction, and clearing the margin before each
 * measurement keeps repeated runs idempotent.
 *
 * Inert unless something is actually hidden — with no insets the effect only
 * clears whatever it applied before.
 */
export const useOverlayViewportClamp = (insets: VisibleInsets) => {
  const { top, bottom } = insets;

  useEffect(() => {
    const roots = () =>
      Array.from(document.querySelectorAll<HTMLElement>(OVERLAY_SELECTOR));

    const clear = () => {
      roots().forEach(root => {
        root.style.marginTop = "";
      });
    };

    if (top === 0 && bottom === 0) {
      clear();
      return;
    }

    let frameId: number | null = null;

    const sync = () => {
      frameId = null;
      const band = { top, bottom: window.innerHeight - bottom };

      roots().forEach(root => {
        const surface =
          root.querySelector<HTMLElement>(SURFACE_SELECTOR) ??
          (root.firstElementChild as HTMLElement | null);
        if (!surface) return;

        // Measure the overlay where MUI put it, not where we last moved it.
        if (root.style.marginTop) root.style.marginTop = "";

        const offset = overlayClampOffset(
          surface.getBoundingClientRect(),
          band,
        );
        root.style.marginTop = offset ? `${-offset}px` : "";
      });
    };

    const schedule = () => {
      if (frameId === null) frameId = requestAnimationFrame(sync);
    };

    /*
     * Overlays are portalled straight into the body, so their arrival is a
     * top-level childList change. Coalesced into one frame because opening a
     * Select adds the root and its paper in the same batch.
     */
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true });

    // The band itself moved, so anything already open needs re-checking.
    schedule();

    return () => {
      observer.disconnect();
      if (frameId !== null) cancelAnimationFrame(frameId);
      clear();
    };
  }, [top, bottom]);
};
